import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, computed, input, output, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { VxButtonComponent } from '@ui/button/button.component';
import { VxCardHeaderComponent } from '@ui/card/card-header.component';
import { VxCardComponent } from '@ui/card/card.component';
import { VxIconButtonComponent } from '@ui/icon-button/icon-button.component';
import { VxIconComponent } from '@ui/icon/icon.component';
import { VxMenuItemComponent } from '@ui/menu/menu-item.component';
import { VxMenuTriggerDirective } from '@ui/menu/menu-trigger.directive';
import { VxMenuComponent } from '@ui/menu/menu.component';
import { VxProgressRingComponent } from '@ui/progress-ring/progress-ring.component';
import { GettingStartedItem, GettingStartedItemKey } from './getting-started';

/**
 * "Get started" after onboarding (the Framer dashboard's accordion): done
 * items as single lines, the next open item expanded with what it is and
 * its action, the rest collapsed until picked. The dashboard decides the
 * items and their state (`gettingStartedItems`); this card only presents
 * them and reports what the user asked for.
 */
@Component({
  selector: 'app-getting-started-checklist',
  templateUrl: './getting-started-checklist.component.html',
  imports: [
    NgClass,
    NgTemplateOutlet,
    RouterLink,
    TranslocoModule,
    VxButtonComponent,
    VxCardComponent,
    VxCardHeaderComponent,
    VxIconButtonComponent,
    VxIconComponent,
    VxMenuComponent,
    VxMenuItemComponent,
    VxMenuTriggerDirective,
    VxProgressRingComponent,
  ],
  host: { class: 'block' },
})
export class GettingStartedChecklistComponent {
  readonly items = input.required<GettingStartedItem[]>();

  readonly dismissed = output<void>();

  /** "Create your first incident" opens a prefilled test incident. */
  readonly testIncidentRequested = output<void>();

  /** The item the user opened or closed; undefined until they pick one. */
  private readonly picked = signal<GettingStartedItemKey | null | undefined>(
    undefined
  );

  /** Expanded item: the user's pick, else the first one still open. */
  protected readonly expanded = computed(() => {
    const picked = this.picked();
    return picked !== undefined
      ? picked
      : (this.items().find(item => !item.done)?.key ?? null);
  });

  protected readonly progress = computed(() => ({
    done: this.items().filter(item => item.done).length,
    total: this.items().length,
  }));

  protected toggle(key: GettingStartedItemKey): void {
    this.picked.set(this.expanded() === key ? null : key);
  }
}
